import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'framework7-react';
import { getIcon } from './utils';
import II18 from '../translate/II18';
import PaperflyAPI from '../../server/api/PaperflyAPI';
import { forceFlushTelemetry, recordAppError } from '../../telemetry/otel';

const MAX_ATTR_LENGTH = 4000;

const cut = value => String(value || '').slice(0, MAX_ATTR_LENGTH);

/**
 * React 16 (prod) catches render errors itself: they never reach
 * window.onerror, so the boundary reports them to OTel directly.
 */
export function reportCrashToTelemetry(error, errorInfo) {
  const isError = typeof error === 'object' && error !== null;
  recordAppError(
    'react.error_boundary',
    isError ? error.message || String(error) : String(error),
    {
      'error.type': isError ? error.name || '' : typeof error,
      'error.stack': cut(isError ? error.stack : ''),
      'error.component_stack': cut(errorInfo && errorInfo.componentStack),
      'user_agent.original':
        typeof navigator !== 'undefined' ? navigator.userAgent : '',
      'app.online': typeof navigator !== 'undefined' ? navigator.onLine : true
    }
  );
  Promise.resolve(forceFlushTelemetry()).catch(() => {});
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, showStack: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    reportCrashToTelemetry(error, errorInfo);
    this.setState(
      {
        error,
        errorInfo
      },
      () =>
        PaperflyAPI.submitErrorProtocol(
          window.recipientRequestId,
          'APP_CRASH',
          this.crashProtocol()
        )
    );
  }

  crashProtocol = () =>
    [
      this.errorStack(),
      'Component stack:' +
        ((this.state.errorInfo && this.state.errorInfo.componentStack) || ''),
      'User agent: ' +
        (typeof navigator !== 'undefined' ? navigator.userAgent : '')
    ].join('\r\n');

  pretty(str, is_xhtml) {
    return str
      ? '<pre>' + str.replace(/\\n/g, '<br/>').replace(/,/g, '<br/>') + '</pre>'
      : '';
  }

  errorStack = () =>
    typeof this.state.error === 'object' && this.state.error !== null
      ? JSON.stringify(
          this.state.error,
          Object.getOwnPropertyNames(this.state.error)
        )
      : this.state.error;

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 20000,
            overflow: 'auto',
            background: '#3a3a3a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              maxWidth: window.isDesktop ? 700 : undefined,
              maxHeight: window.isDesktop ? 500 : undefined,
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                width: '100%',
                minHeight: 125,
                height: '30%',
                display: 'flex',
                background: '#ef4949',
                color: '#fff',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {getIcon('material:sentiment_very_dissatisfied', 80)}
            </div>

            <div
              style={{
                fontSize: 22,
                padding: '40px 20px',
                textAlign: 'center'
              }}
            >
              {II18.get('app_fatal_err')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                style={{ width: 'fit-content' }}
                raised
                onClick={() => {
                  if (this.props.onReloadApp) {
                    this.props.onReloadApp();
                    this.setState({ hasError: false });
                  } else window.location.reload();
                }}
              >
                {II18.get('app_restart_app')}
              </Button>
            </div>

            <div
              style={{
                alignItems: 'center',
                padding: '10px 10px',
                color: '#999',
                border: '1px solid #eee',
                margin: 20,
                marginTop: 40,
                marginBottom: 0,
                borderRadius: 10,
                background: '#fffbfb',
                height: !this.state.sendMessageMode ? 18 : undefined,
                overflow: !this.state.sendMessageMode ? 'hidden' : '',
                width: !this.state.sendMessageMode
                  ? 'fit-content'
                  : 'calc(100% - 45px)'
              }}
            >
              <div
                onClick={() => this.setState({ sendMessageMode: true })}
                style={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  textAlign: 'center',
                  marginBottom: 10
                }}
              >
                {II18.get('app_send_err')}
              </div>

              {this.state.errorSended ? (
                <label>Ok</label>
              ) : (
                <>
                  <div style={{ width: '100%' }}>
                    <div style={{ fontWeight: 400 }}>
                      {II18.get('app_err_desc')}:
                    </div>
                    <textarea
                      style={{
                        border: '1px solid #efefef',
                        borderRadius: 5,
                        width: '100%',
                        padding: 4
                      }}
                      rows={3}
                      onChange={event => {
                        this.setState({
                          message: event.target.value
                        });
                      }}
                      defaultValue={this.state.message}
                    />
                  </div>

                  <Button
                    disabled={!this.state.message}
                    onClick={() => {
                      PaperflyAPI.submitErrorProtocol(
                        window.recipientRequestId,
                        'USER_ERROR_MESSAGE',
                        this.state.message
                      );
                      this.setState({ errorSended: true });
                    }}
                    style={{ width: 'fit-content', marginTop: 10 }}
                    color="gray"
                    outline
                    round
                  >
                    {II18.get('app_err_send')}
                  </Button>
                </>
              )}
            </div>

            <Button
              onClick={() => this.setState({ showStack: true })}
              style={{ marginTop: 40, fontSize: 6, color: '#bbb' }}
            >
              :show error:
            </Button>
            <div
              style={{ display: this.state.showStack ? 'block' : 'none' }}
              className={'crash-stack'}
            >
              <b>Error:</b>
              <div
                dangerouslySetInnerHTML={{
                  __html: this.pretty(this.errorStack())
                }}
              ></div>
              <b>Stack:</b>
              <div
                dangerouslySetInnerHTML={{
                  __html: this.pretty(JSON.stringify(this.state.errorInfo))
                }}
              ></div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  onReloadApp: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};
